import React, { useState, useRef, useCallback, FC } from "react";
import uploadService from "../../services/upload.service";
import { FileUploaderProps } from "../../types/components";
import { UploadFileType, Attachments, UploadFolder } from "../../types/api"; // Import Attachments
import { useAuth } from "../../context/AuthContext";

const FileUploader: FC<FileUploaderProps> = ({
  label,
  name,
  accept = ".pdf,.jpg,.jpeg,.png,.svg", // Added .svg
  maxSize = 5 * 1024 * 1024, // 5MB default
  onFileUploaded, // Renamed prop
  attachment, // New prop
  required = false,
  description,
  icon,
  uploadType,
  referenceId,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedAttachment, setUploadedAttachement] = useState<
    Attachments | undefined
  >(attachment);
  const [error, setError] = useState<string | undefined>();
  const { user } = useAuth();
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver((prev) => (prev ? prev : true));
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver((prev) => (prev ? false : prev));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver((prev) => (prev ? prev : true));
  }, []);

  const validateFile = (file: File) => {
    const errors: string[] = [];

    // Check file size
    if (file.size > maxSize) {
      errors.push(
        `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`
      );
    }

    // Check file type
    const acceptedTypes = accept.split(",").map((type) => type.trim());
    const extPart = file.name.split(".").pop();
    const fileExtension = extPart ? "." + extPart.toLowerCase() : "";
    const isValidType = acceptedTypes.some(
      (type) =>
        type === fileExtension || file.type.includes(type.replace(".", ""))
    );

    if (!isValidType) {
      errors.push(`File type not supported. Accepted: ${accept}`);
    }

    return errors;
  };

  const getFolderByType = (type: UploadFileType): UploadFolder => {
    switch (type) {
      case "Resume":
      case "CoverLetter":
      case "StudentIdCard":
      case "ProfilePhoto":
        return "User";
      case "CompanyLogo":
      case "CompanyDocumentMSME":
      case "CompanyDocumentPAN":
      case "CompanyDocumentGST":
      case "CompanyDocumentCIN":
        return "Company";
      case "JobDescription":
      case "PostAttachment":
        return "JobPost";
    }
  };
  const uploadAndGetAttachment = async (file: File): Promise<void> => {
    setIsUploading(true);
    setUploadProgress(10);
    setError(undefined);

    try {
      const {
        data: { preSignedUrl: uploadUrl, attachmentId },
      } = await uploadService.getUploadUrl({
        fileName: name,
        contentType: file.type,
        // userId : user?.userId,
        type: uploadType,
        folder: getFolderByType(uploadType),
        referenceId: referenceId,
      });
      setUploadedAttachement({
        attachmentId: attachmentId,
        originalName: name,
        contentType: file.type,
      });
      if (uploadUrl) {
        const xhr = new XMLHttpRequest();

        // await fetch(uploadUrl, {
        //   method: 'PUT',
        //   headers: { 'Content-Type': file.type || 'application/octet-stream'

        //   },
        //   body: file,
        // });
        xhr.open("PUT", uploadUrl, true);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.floor(
              (event.loaded / event.total) * 100
            );
            // setState("progress", percentComplete);
            setUploadProgress(percentComplete);
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            setUploadProgress(100);
            uploadService
              .confirmUpload(attachmentId)
              .then(() => {
                uploadService
                  .getAttachment(attachmentId)
                  .then(({ data: attachmentObj }) => {
                    setUploadedAttachement(attachmentObj);
                    onFileUploaded(attachmentObj);
                  })
                  .catch(() => {
                    setError("Failed to Upload file");
                  })
                  .finally(() => {
                    setIsUploading(false);
                  });
              })
              .catch(() => {
                setError("Failed to Upload file");
              });
          } else {
            setError("Failed to Upload file");
            setIsUploading(false);
          }
        };

        xhr.onerror = () => {
          setIsUploading(false);
          setError("Failed to Upload file");
          setUploadedAttachement(undefined);
          // setState("uploadStatus", "idle");
          // showSnackbar("An error occurred during the upload.", true);
        };

        xhr.send(file);
      }
    } catch (e) {
      console.error("Upload failed", e);
      setError("Failed to Upload file");
      setIsUploading(false);
      setUploadedAttachement(undefined);
      return;
    }
  };

  const handleFileSelection = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const validationErrors: string[] = validateFile(file);
    if (validationErrors.length > 0) {
      // Handle validation errors
      console.error("File validation errors:", validationErrors);
      return;
    }
    console.error("File Triggering Uplaod Url", file);
    await uploadAndGetAttachment(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files: File[] = Array.from(e.dataTransfer.files || []);
      handleFileSelection(files);
    },
    []
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = Array.from(e.target.files || []);
    handleFileSelection(files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChangeFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the file input
    }
    fileInputRef.current?.click(); // Open file dialog
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFilePreview = (file: Attachments) => {
    if (file.fileUrl == undefined) return null;
    const fileName = file.originalName;
    const contentType = file.contentType;
    const fileUrl = file.fileUrl;

    if (!contentType) return null;

    if (contentType.startsWith("image/")) {
      return (
        <img
          src={fileUrl}
          alt={fileName}
          className="w-full h-full object-cover rounded-lg"
        />
      );
    } else if (contentType === "application/pdf") {
      return (
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full h-full flex items-center justify-center"
        >
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
            <svg
              className="w-6 h-6 text-red-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </a>
      );
    } else {
      return (
        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
          <svg
            className="w-6 h-6 text-gray-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      );
    }
  };

  let displayFile = uploadedAttachment;
  let displayFileName = uploadedAttachment?.originalName;
  let displayFileSize = "";
  let displayContentType = uploadedAttachment?.contentType;

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-semibold text-gray-800 mb-3">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}

      <div
        className={`relative w-full border-2 border-dashed rounded-xl transition-all duration-300 cursor-pointer group ${
          isDragOver
            ? "border-green-400 bg-green-50"
            : displayFile && !error
            ? "border-green-300 bg-green-50"
            : error
            ? "border-red-300 bg-red-50"
            : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={displayFile ? undefined : handleClick} // Only allow click to open file dialog if no file is displayed
      >
        <input
          ref={fileInputRef}
          type="file"
          name={name}
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
        />

        {displayFile ? (
          // File Selected/Preview State - Horizontal Layout
          <div className="p-4">
            <div className="items-center justify-between">
              <div className="flex min-h-20 items-center justify-between min-w-0">
                <div className="flex-shrink-0 flex-none w-12 h-12 mr-4">
                  {getFilePreview(displayFile) || (
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-8 h-8"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="grow">
                  <p className="text-l font-semibold text-gray-900">
                    {displayFileName}
                  </p>
                  <div className="flex items-center space-x-4 mt-1">
                    {!isUploading && (
                      <div className="flex items-center text-xs text-green-600">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Submitted
                      </div>
                    )}
                  </div>
                  {isUploading && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>
                          {uploadProgress != 100 ? "Uploading..." : "Uploaded"}
                        </span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`${
                            error ? `bg-red-300` : `bg-primary-lightGreen`
                          } h-1.5 rounded-full transition-all duration-300`}
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {error && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {error}
                    </p>
                  )}
                </div>
                <div className="flex-none items-center space-x-2 ml-4">
                  <button
                    type="button"
                    onClick={handleChangeFile}
                    className="flex-shrink-0 p-4 text-gray-400 hover:text-gray-500 transition-colors duration-200 rounded-xl bg-gray-100 hover:bg-gray-200"
                    title="Change file"
                  >
                    <svg
                      className="w-6 h-6 "
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex-none items-center space-x-2 ml-4">
                  <button
                    type="button"
                    onClick={() =>
                      window.open(`${displayFile?.fileUrl}`, "_blank")
                    }
                    className="flex-shrink-0 p-4 text-gray-400 hover:text-gray-500 transition-colors duration-200 rounded-xl bg-gray-100 hover:bg-gray-200"
                    title="Change file"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    >
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M4 6l-2-2M20 6l2-2M4 18l-2 2M20 18l2 2"></path>
                    </svg>
                    {/* <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                  </svg> */}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Empty State - Horizontal Layout
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6 flex-1">
                <div className="flex-shrink-0">
                  {icon || (
                    <div
                      className={`
                        w-16 h-16 rounded-xl flex items-center justify-center transition-colors duration-300
                        "bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-500"
                      `}
                    >
                      <svg
                        className="w-8 h-8"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p
                    className={`text-xl font-semibold transition-colors duration-300  "text-gray-800"`}
                  >
                    {"Drag & drop your file here"}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Supports: {accept} • Max size:{" "}
                    {Math.round(maxSize / (1024 * 1024))}MB
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Drag overlay */}
        {isDragOver && (
          <div className="absolute inset-0 bg-green-200 rounded-xl flex items-center justify-center">
            <div className="text-green-600 font-medium text-lg flex items-center space-x-2">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <span>Drop to upload</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;
